import type { AddressOrNative } from '@cronos-app/sdk';
import { NATIVE_TOKEN_IDENTIFIER, ZKCRO_DECIMALS } from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { RefetchInterval } from '@ui/config/ui';
import { useSDK } from '@ui/utils';
import { Fraction } from 'bi-fraction';
import { useCallback } from 'react';

interface UseTokenBalanceOptions {
  tokenAddress: AddressOrNative | undefined;
  account?: string | null;
  enabled?: boolean;
}

export const useAgentFunBalance = () => {
  const sdk = useSDK();
  return useTokenBalance({
    tokenAddress: sdk.getContractAddress('AgentFun'),
  });
};

export const useTokenBalance = ({
  tokenAddress,
  account: _account,
  enabled,
}: UseTokenBalanceOptions): UseQueryResult<Fraction> => {
  const sdk = useSDK();
  const myAccount = currentWallet.useAccount();
  const provider = currentWallet.useProvider();

  const account = _account ?? myAccount;

  const getNativeBalance = useCallback(async () => {
    if (!account) throw new Error('account is empty');
    if (!provider) throw new Error('provider is empty');
    const balance = await provider.getBalance(account);
    return new Fraction(balance).shr(ZKCRO_DECIMALS);
  }, [account, provider]);

  return useQuery({
    queryKey: [
      TOKEN_BALANCE_QUERY_KEY,
      tokenAddress,
      account,
      sdk,
      getNativeBalance,
    ],
    queryFn: async () => {
      if (!tokenAddress) throw new Error('tokenAddress is empty');
      if (!account) throw new Error('account is empty');

      if (tokenAddress === NATIVE_TOKEN_IDENTIFIER) {
        return getNativeBalance();
      }

      const erc20Contract = sdk.getERC20(tokenAddress);

      const [balance, decimals] = await Promise.all([
        erc20Contract.balanceOf(account),
        erc20Contract.decimals(),
      ]);

      return new Fraction(balance).shr(Number(decimals));
    },
    staleTime: RefetchInterval.Slow,
    refetchInterval: RefetchInterval.Slow,
    enabled: enabled === false ? false : !!account && !!tokenAddress,
  });
};

export const getTokenBalanceQueryKey = (tokenAddress?: AddressOrNative) =>
  tokenAddress
    ? ([TOKEN_BALANCE_QUERY_KEY, tokenAddress] as const)
    : ([TOKEN_BALANCE_QUERY_KEY] as const);

const TOKEN_BALANCE_QUERY_KEY = 'useTokenBalance';
